import { useEffect, useRef, useState } from 'react'
import {
  civilParts,
  formatStoryWhen,
  parseStoryWhen,
  storyIsoOnCivilDate,
  type StoryWhenValue,
} from './storyTime'

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function range(lo: number, hi: number): number[] {
  const out: number[] = []
  for (let n = lo; n <= hi; n++) out.push(n)
  return out
}

type CivilDraft = {
  year: number
  month: number
  day: number
  hour12: number
  minute: number
  second: number
  am: boolean
}

function civilFromIso(iso: string, timeZone: string): CivilDraft {
  const p = civilParts(iso, timeZone)
  const am = p.hour < 12
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12
  return {
    year: p.year,
    month: p.month,
    day: p.day,
    hour12,
    minute: p.minute,
    second: p.second,
    am,
  }
}

function civilNow(timeZone: string): CivilDraft {
  return civilFromIso(new Date().toISOString(), timeZone)
}

function draftToIso(draft: CivilDraft, timeZone: string, withSeconds: boolean): string {
  const dayMax = daysInMonth(draft.year, draft.month)
  const day = Math.min(draft.day, dayMax)
  let hour = draft.hour12 % 12
  if (!draft.am) hour += 12
  return storyIsoOnCivilDate(
    draft.year,
    draft.month,
    day,
    hour,
    draft.minute,
    withSeconds ? draft.second : 0,
    timeZone,
  )
}

/**
 * Story start/end. Typed clock, optional date, or a discrete calendar/time picker
 * (finite hour/min/sec ranges — no continuous spinning wheels).
 */
export function StoryWhenInput({
  iso,
  dateSet,
  timeZone,
  withSeconds,
  allowEmpty,
  /** Time-only values land on the next occurrence after this instant. */
  afterIso,
  disabled,
  flash,
  id,
  onCommit,
}: {
  iso?: string | null
  dateSet?: boolean
  timeZone: string
  withSeconds: boolean
  allowEmpty?: boolean
  afterIso?: string
  disabled?: boolean
  flash?: boolean
  id?: string
  /** Error string keeps the typed value. Null accepts it. */
  onCommit: (value: StoryWhenValue | null) => string | null
}) {
  const display = iso
    ? formatStoryWhen(iso, timeZone, { withDate: dateSet !== false, withSeconds })
    : ''
  const [text, setText] = useState(display)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<CivilDraft>(() =>
    iso ? civilFromIso(iso, timeZone) : civilNow(timeZone),
  )
  const focusedRef = useRef(false)
  const focusTextRef = useRef(display)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusedRef.current) return
    setText(display)
    setError(null)
  }, [display])

  useEffect(() => {
    if (!open) return
    setDraft(iso ? civilFromIso(iso, timeZone) : civilNow(timeZone))
    const onDoc = (event: MouseEvent): void => {
      const root = wrapRef.current
      if (!root || !(event.target instanceof Node) || root.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, iso, timeZone])

  function commit(raw: string): void {
    const parsed = parseStoryWhen(raw, timeZone, { afterIso })
    if (parsed === undefined) {
      setError('Use 10:50 AM or Sep 22, 2026 10:50 AM')
      return
    }
    if (parsed == null && !allowEmpty) {
      setError('Start time is required')
      setText(display)
      return
    }
    const message = onCommit(parsed)
    if (message) {
      setError(message)
      return
    }
    setError(null)
    if (parsed == null) setText('')
  }

  function applyDraft(next: CivilDraft): void {
    const dayMax = daysInMonth(next.year, next.month)
    const clamped = next.day > dayMax ? { ...next, day: dayMax } : next
    setDraft(clamped)
    const nextIso = draftToIso(clamped, timeZone, withSeconds)
    const message = onCommit({ iso: nextIso, dateSet: true })
    setError(message)
    if (!message) {
      setText(formatStoryWhen(nextIso, timeZone, { withDate: true, withSeconds }))
    }
  }

  const yearOptions = (() => {
    const base = draft.year
    const years = new Set<number>()
    for (let y = base - 3; y <= base + 5; y++) years.add(y)
    if (iso) years.add(civilParts(iso, timeZone).year)
    return [...years].sort((a, b) => a - b)
  })()

  const dayMax = daysInMonth(draft.year, draft.month)

  return (
    <div className="sc-elapsed-wrap" ref={wrapRef}>
      <div className="sc-when-row">
        <input
          id={id}
          className={`sc-fin${error ? ' bad' : ''}${flash ? ' sc-field-flash' : ''}`}
          value={text}
          placeholder={
            withSeconds
              ? '10:50:00 AM or Sep 22, 2026 10:50:00 AM'
              : '10:50 AM or Sep 22, 2026 10:50 AM'
          }
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={Boolean(error)}
          onFocus={() => {
            focusedRef.current = true
            focusTextRef.current = text
          }}
          onChange={(e) => {
            setText(e.target.value)
            if (error) setError(null)
          }}
          onBlur={(e) => {
            focusedRef.current = false
            const raw = e.target.value
            if (raw.trim() === focusTextRef.current.trim()) {
              setText(display)
              setError(null)
              return
            }
            commit(raw)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
        />
        <button
          type="button"
          className={`sc-when-pick${open ? ' on' : ''}`}
          disabled={disabled}
          aria-label="Choose date and time"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M7 2h2v2h6V2h2v2h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3V2zm13 8H4v10h16V10z"
            />
          </svg>
        </button>
      </div>
      {open ? (
        <div className="sc-when-panel" role="dialog" aria-label="Date and time">
          <div className="sc-when-panel-row">
            <label className="sc-when-col">
              <span>Month</span>
              <select
                value={draft.month}
                onChange={(e) => applyDraft({ ...draft, month: Number(e.target.value) })}
              >
                {MONTHS.map((label, i) => (
                  <option key={label} value={i + 1}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sc-when-col">
              <span>Day</span>
              <select
                value={Math.min(draft.day, dayMax)}
                onChange={(e) => applyDraft({ ...draft, day: Number(e.target.value) })}
              >
                {range(1, dayMax).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="sc-when-col sc-when-col-year">
              <span>Year</span>
              <select
                value={draft.year}
                onChange={(e) => applyDraft({ ...draft, year: Number(e.target.value) })}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="sc-when-panel-row">
            <label className="sc-when-col">
              <span>Hour</span>
              <select
                value={draft.hour12}
                onChange={(e) => applyDraft({ ...draft, hour12: Number(e.target.value) })}
              >
                {range(1, 12).map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </label>
            <label className="sc-when-col">
              <span>Min</span>
              <select
                value={draft.minute}
                onChange={(e) => applyDraft({ ...draft, minute: Number(e.target.value) })}
              >
                {range(0, 59).map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </label>
            {withSeconds ? (
              <label className="sc-when-col">
                <span>Sec</span>
                <select
                  value={draft.second}
                  onChange={(e) => applyDraft({ ...draft, second: Number(e.target.value) })}
                >
                  {range(0, 59).map((s) => (
                    <option key={s} value={s}>
                      {String(s).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="sc-when-col">
              <span> </span>
              <select
                value={draft.am ? 'am' : 'pm'}
                onChange={(e) => applyDraft({ ...draft, am: e.target.value === 'am' })}
                aria-label="AM or PM"
              >
                <option value="am">AM</option>
                <option value="pm">PM</option>
              </select>
            </label>
          </div>
        </div>
      ) : null}
      {error ? <span className="sc-elapsed-err">{error}</span> : null}
    </div>
  )
}
